import {
  Directive,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import isEqual from 'lodash/isEqual';
import { Props } from 'tippy.js';

import Tooltip from './Tooltip';

@Directive({
  selector: '[ppTooltip]',
  standalone: false,
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input() ppOptions: Partial<Props> = {};
  @Input() ppTitle = '';
  @Input() ppMobileTooltip = false;
  @Input() ppTrimTooltip = false;
  @Input() ppChildTooltip = false;
  @Input() ppSanitize = true;
  @Input() ppInteractive = false;

  public _element: HTMLElement;
  public _host: HTMLElement;
  isTrimmed = true;

  tooltip: Tooltip;
  private tooltipInitialized = false;
  private mouseEnterHandler: () => void;

  constructor(
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {
    this._element = this.elementRef.nativeElement;
    this._host = this.elementRef.nativeElement;
  }

  ngOnInit(): void {
    this.mouseEnterHandler = (): void => this.initTooltipOnHover();

    this.ngZone.runOutsideAngular(() => {
      this._element.addEventListener('mouseenter', this.mouseEnterHandler, { once: true });
    });
  }

  private initTooltipOnHover(): void {
    if (!this.tooltipInitialized) {
      this.tooltipInitialized = true;
      this.createTooltipInstance({ showOnReady: true });
    }
  }

  private createTooltipInstance({ showOnReady = false }: { showOnReady?: boolean } = {}): void {
    this.ngZone.runOutsideAngular(() => {
      if (this.tooltip) {
        this.tooltip.removeTooltip();
      }

      const options = this.ppSanitize
        ? this.ppOptions
        : { ...this.ppOptions, content: this.ppTitle, allowHTML: true };

      this.tooltip = new Tooltip({
        options,
        title: this.ppSanitize ? this.ppTitle : '',
        mobileTooltip: this.ppMobileTooltip,
        trimTooltip: this.ppTrimTooltip,
        childTooltip: this.ppChildTooltip,
        element: this._element,
        interactive: this.ppInteractive,
      });

      this.tooltip.createTooltip(() => {
        if (showOnReady && this._element.matches(':hover')) {
          this.tooltip._element._tippy?.show();
        }
      });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.tooltipInitialized) {
      return;
    }

    const hasRealChange = Object.values(changes).some(
      (change) => !change.firstChange && !isEqual(change.previousValue, change.currentValue),
    );

    if (hasRealChange) {
      this.updateTooltipInstance();
    }
  }

  private updateTooltipInstance(): void {
    if (!this.tooltip) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const options = this.ppSanitize
        ? this.ppOptions
        : { ...this.ppOptions, content: this.ppTitle, allowHTML: true };

      this.tooltip.update({
        options,
        title: this.ppSanitize ? this.ppTitle : '',
        mobileTooltip: this.ppMobileTooltip,
        trimTooltip: this.ppTrimTooltip,
        childTooltip: this.ppChildTooltip,
        interactive: this.ppInteractive,
      });
    });
  }

  ngOnDestroy(): void {
    this.ngZone.runOutsideAngular(() => {
      if (!this.tooltipInitialized && this.mouseEnterHandler) {
        this._element.removeEventListener('mouseenter', this.mouseEnterHandler);
      }

      if (this.tooltip) {
        this.tooltip.removeTooltip();
      }
    });
  }
}
